import axios from 'axios';

/**
 * Feb 2026 — Global axios 401 interceptor.
 *
 * When ANY backend call returns 401 (token expired / invalidated /
 * bad token), we:
 *   1. Clear every known session token from localStorage.
 *   2. Redirect the user to the most appropriate login screen for
 *      the section of the app they're currently in.
 *
 * Login endpoints themselves are exempt — a wrong-password 401 must
 * render inline in the form, not nuke the entire session.
 *
 * install401Interceptor() is idempotent; calling it more than once is
 * a no-op so hot-reload doesn't stack duplicate handlers.
 */
const TOKEN_KEYS = [
  'mr_admin_token',
  'mr_admin_email',
  'mr_admin_attendance',
  'branch_token',
  'branch_profile',
  'branch_desk_token',
  'branch_desk_profile',
  'mr_driver_token',
  'mr_customer_token',
];

// URLs where a 401 is a NORMAL rejection response (wrong password),
// not an expired-token event. We match by suffix so we're immune to
// the REACT_APP_BACKEND_URL prefix.
const LOGIN_URL_SUFFIXES = [
  '/api/admin/login',
  '/api/branch/login',
  '/api/branch/desk-tracker/login',
  '/api/driver/login',
  '/api/driver/signup',
  '/api/auth/login',
  '/api/auth/register',
  '/api/auth/otp/verify',
  '/api/auth/otp/request',
];

const isLoginRequest = (url) => {
  if (!url || typeof url !== 'string') return false;
  return LOGIN_URL_SUFFIXES.some((suffix) => url.endsWith(suffix));
};

const clearAllTokens = () => {
  try {
    for (const key of TOKEN_KEYS) {
      localStorage.removeItem(key);
    }
    // Session-scoped mirrors of the above.
    try { sessionStorage.removeItem('mr_admin_attendance'); } catch { /* quota */ }
    try { sessionStorage.removeItem('mr_branch_attendance'); } catch { /* quota */ }
  } catch {
    /* private mode / blocked storage — nothing else we can do */
  }
};

const loginRedirectFor = (pathname) => {
  const p = (pathname || '').toLowerCase();
  if (p.startsWith('/warehouse')) return '/warehouse/login';
  if (p.startsWith('/admin')) return '/admin/login';
  if (p.startsWith('/branch/desk-tracker')) return '/branch/desk-tracker/login';
  if (p.startsWith('/branch')) return '/branch/login';
  if (p.startsWith('/driver')) return '/driver/login';
  return '/login';
};

let installed = false;

export function install401Interceptor() {
  if (installed) return;
  installed = true;

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      const url = error?.config?.url || '';
      if (status === 401 && !isLoginRequest(url)) {
        clearAllTokens();
        if (typeof window !== 'undefined') {
          const target = loginRedirectFor(window.location.pathname);
          // Only redirect if we're not already on the target login
          // page — otherwise the login form would reload itself when
          // the user types a wrong password on a non-listed endpoint.
          const current = (window.location.pathname || '').replace(/\/+$/, '') || '/';
          const normalisedTarget = target.replace(/\/+$/, '') || '/';
          if (current !== normalisedTarget) {
            // Feb 2026 — preserve the intended destination for the
            // warehouse floor app so re-auth lands back on the same
            // page (and keeps the /warehouse PWA scope active on
            // handhelds). AdminLogin (also mounted at
            // /warehouse/login) already reads `?next=`.
            let assignTo = target;
            try {
              const p = (window.location.pathname || '');
              if (p.toLowerCase().startsWith('/warehouse') && !p.toLowerCase().startsWith('/warehouse/login')) {
                const dest = `${p}${window.location.search || ''}`;
                assignTo = `${target}?next=${encodeURIComponent(dest)}`;
              }
            } catch { /* fall back to bare target */ }
            // Full navigation so any React state referencing the old
            // token is torn down cleanly.
            window.location.assign(assignTo);
          }
        }
      }
      return Promise.reject(error);
    },
  );
}

export const _internal = { TOKEN_KEYS, LOGIN_URL_SUFFIXES, loginRedirectFor };
